import {
  API_URL,
  getAccessToken,
  getActingOrganizationId,
} from "@/api/_client";
import { ChatMessageType } from "@/api/chat";
import { useUser } from "@/api/user";
import { useQueryClient } from "@tanstack/react-query";
import React, { useCallback } from "react";
import useWebSocket, { ReadyState } from "react-use-websocket";
import {
  AgentContext,
  AgentViewDescription,
  OutgoingAgentMessage,
} from "./agent-provider";
import { useAppContext } from "./app-context";
import { useNavigate } from "react-router-dom";
import { applyIncomingAgentMessage } from "./agent-messages";

const AGENT_WEBSOCKET_URL = API_URL + "ws/agent";

/** A UI-only "event" bubble (connection notices, handoff markers) — not a real
 *  chat message, so it carries no user and no sources. */
const eventBubble = (content: string): ChatMessageType => ({
  sender: "event",
  content,
  user: { id: "", first_name: "", last_name: "", email: "" },
  sources: [],
});

export const AgentProvider = ({ children }: { children: React.ReactNode }) => {
  const { currentChatSessionId, chatOrganizationId } = useAppContext();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [usersTyping, setUsersTyping] = React.useState<
    { first_name: string; last_name: string; id: string }[]
  >([]);
  const user = useUser();
  const [shouldConnect, setShouldConnect] = React.useState(false);

  const [messages, setMessages] = React.useState<ChatMessageType[]>([]);
  const typingTimeouts = React.useRef<Map<string, NodeJS.Timeout>>(new Map());
  const [currentViewDescription, setCurrentViewDescription] =
    React.useState<AgentViewDescription>(null);
  const processedNavigations = React.useRef<Set<string>>(new Set());
  const activeDashboardIdRef = React.useRef<string | null>(null);
  // New-chat race guard: true once a message has actually been transmitted and
  // is awaiting its reply (NOT merely queued — a queued send may still be
  // cancelled by New chat, in which case no reply is coming).
  const awaitingResponseRef = React.useRef(false);
  // Send-retry intervals for messages queued while the socket was down. New chat
  // cancels them so a message typed before "New chat" never transmits into the
  // fresh conversation (the "message waiting for the socket" half of the race).
  const pendingSendIntervalsRef = React.useRef<Set<ReturnType<typeof setInterval>>>(
    new Set(),
  );
  // Armed by clearConversation when a reply is in flight, so that stale reply is
  // dropped instead of repopulating the freshly-cleared conversation.
  const suppressNextAiResponseRef = React.useRef(false);
  // Processed incoming ids — makes the effect body idempotent under React
  // StrictMode's double-invoke (and server duplicates), so the refs above are
  // mutated exactly once per message.
  const processedIncomingIdsRef = React.useRef<Set<string>>(new Set());

  // Clear agent messages when navigating to a different resource (e.g.,
  // switching dashboards). Uses a stable resource key (dashboard_id,
  // project_id) so toggling between widget-level and dashboard-level context
  // within the same resource does NOT clear messages — the conversation
  // history stays visible.
  const prevNavigationKeyRef = React.useRef<string | null>(null);
  React.useEffect(() => {
    const vars = currentViewDescription?.known_vars;
    // Use the primary resource ID as the stable key. Widget context adds
    // widget_id but keeps the same dashboard_id, so transitions like
    // widget→dashboard after an escalation response won't wipe history.
    const navigationKey = vars
      ? (vars.dashboard_id as string) ??
        (vars.project_id as string) ??
        JSON.stringify(vars)
      : null;
    if (
      prevNavigationKeyRef.current !== null &&
      navigationKey !== prevNavigationKeyRef.current
    ) {
      setMessages([]);
      processedNavigations.current.clear();
      // The id-dedup set only needs to cover messages currently in the list;
      // reset it with the conversation so it can't grow across resource switches.
      processedIncomingIdsRef.current.clear();
    }
    prevNavigationKeyRef.current = navigationKey;
    activeDashboardIdRef.current =
      (vars?.dashboard_id as string) ?? null;
  }, [currentViewDescription]);

  // Just past the backend's 120s AI_RESPONSE_TIMEOUT. Was 5 minutes, which left
  // the 'thinking' dots stuck for up to 5 min if a typing-off event was lost.
  const TYPING_TIMEOUT_DURATION = 130 * 1000;

  const removeUserFromTyping = useCallback((userId: string) => {
    const existingTimeout = typingTimeouts.current.get(userId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
      typingTimeouts.current.delete(userId);
    }
    setUsersTyping((oldUsers) => oldUsers.filter((user) => user.id !== userId));
  }, []);

  const token = getAccessToken();
  const url = new URL(AGENT_WEBSOCKET_URL);
  url.searchParams.append("token", token || "");
  // Admin home (TOP_HOST) has no cookie-based acting org — callers set
  // chatOrganizationId via AppContext to nominate which org the agent
  // should act as. Backend membership is validated at the WS handshake
  // (authentication_class.py), so a user can't chat with an org they
  // don't belong to.
  const orgId = chatOrganizationId || getActingOrganizationId();
  url.searchParams.append("organization_id", orgId || "");

  const { sendJsonMessage, lastMessage, readyState } = useWebSocket(
    url.href,
    {
      shouldReconnect: () => true,
    },
    shouldConnect,
  );

  const submitEvent = useCallback(
    (event: string | object) => {
      if (currentChatSessionId && token) {
        sendJsonMessage({ event: event });
      }
    },
    [currentChatSessionId, token, sendJsonMessage],
  );

  React.useEffect(() => {
    if (!currentChatSessionId) return;
    // Cleanup typing timeouts when chat session changes
    const timeouts = typingTimeouts.current;
    return () => {
      timeouts.forEach((timeout) => clearTimeout(timeout));
      timeouts.clear();
      setUsersTyping([]);
    };
  }, [currentChatSessionId]);

  const canSendMessages = readyState === ReadyState.OPEN;
  const canSendMessagesRef = React.useRef(canSendMessages);

  React.useEffect(() => {
    canSendMessagesRef.current = canSendMessages;
  }, [canSendMessages]);

  // Surface connection drops. If the socket was OPEN and then closes while the
  // assistant is mid-answer, clear the stuck 'thinking' dots and tell the user
  // — instead of leaving them staring at bouncing dots for minutes after a wifi
  // blip or a server deploy (the reply is gone; history is client-side only).
  const prevReadyStateRef = React.useRef(readyState);
  React.useEffect(() => {
    const prev = prevReadyStateRef.current;
    prevReadyStateRef.current = readyState;
    if (
      shouldConnect &&
      prev === ReadyState.OPEN &&
      readyState !== ReadyState.OPEN
    ) {
      const wasMidAnswer = usersTyping.length > 0;
      typingTimeouts.current.forEach((t) => clearTimeout(t));
      typingTimeouts.current.clear();
      setUsersTyping([]);
      if (wasMidAnswer) {
        setMessages((prev) => [
          ...prev,
          eventBubble(
            "The connection dropped while I was answering — your last question may not have gone through. Please resend.",
          ),
        ]);
      }
    }
  }, [readyState, shouldConnect, usersTyping]);

  React.useEffect(() => {
    if (!lastMessage || !lastMessage.data) return;
    // Parse the last message data and handle different message types
    const messageData = JSON.parse(lastMessage.data);
    // if the user is "typing" and just received a message, clear their typing status
    if (messageData.type === "chat_message") {
      removeUserFromTyping(messageData.user?.id);
    }
    if (
      messageData.sender === "event" &&
      "is_typing" in (messageData.events || {})
    ) {
      // Handle typing events
      if (messageData.events.is_typing) {
        if (messageData.user?.id === user.data?.id) {
          // If the user is typing, we don't want to add them to the typing list
          return;
        }

        // Clear any existing timeout for this user
        const existingTimeout = typingTimeouts.current.get(
          messageData.user?.id,
        );
        if (existingTimeout) {
          clearTimeout(existingTimeout);
        }

        setUsersTyping((oldUsers) => {
          if (!oldUsers.map((ou) => ou.id).includes(messageData.user?.id)) {
            return [...oldUsers, messageData.user];
          }
          return oldUsers;
        });

        const timeout = setTimeout(() => {
          removeUserFromTyping(messageData.user?.id);
        }, TYPING_TIMEOUT_DURATION);

        typingTimeouts.current.set(messageData.user?.id, timeout);
      } else {
        removeUserFromTyping(messageData.user?.id);
      }
      return;
    }
    // Drop messages scoped to a different dashboard (stale in-flight responses)
    if (
      messageData.scope_dashboard_id &&
      activeDashboardIdRef.current &&
      messageData.scope_dashboard_id !== activeDashboardIdRef.current
    ) {
      return;
    }
    // Idempotency guard: React StrictMode double-invokes this effect (and the
    // server can redeliver), but the ref mutations below must run exactly once
    // per message. Chat messages carry a stable id; skip any we've already seen.
    if (messageData.type === "chat_message" && messageData.id) {
      if (processedIncomingIdsRef.current.has(messageData.id)) return;
      processedIncomingIdsRef.current.add(messageData.id);
    }
    // New-chat race: an AI reply (a chat_message with no user id) that lands
    // after the user started a fresh conversation must be dropped, not shown in
    // the now-empty chat. suppressNextAiResponseRef is armed by clearConversation
    // only while a reply was actually pending, so we disarm exactly one stale reply.
    const isAiMessage =
      messageData.type === "chat_message" && !messageData.user?.id;
    const suppressAiResponse =
      isAiMessage && suppressNextAiResponseRef.current;
    if (isAiMessage) {
      suppressNextAiResponseRef.current = false;
      awaitingResponseRef.current = false;
    }
    // handle messages (optimistic-echo dedup + id dedup + stale-reply suppression
    // live in the pure applyIncomingAgentMessage so they're unit-tested)
    setMessages((oldMessages) =>
      applyIncomingAgentMessage(oldMessages ?? [], messageData, {
        suppressAiResponse,
      }),
    );
    if (
      messageData.type === "event" &&
      (messageData.events?.users_added?.length ||
        messageData.events?.users_removed?.length)
    ) {
      queryClient.invalidateQueries({ queryKey: ["chat-sessions"] });
    }
    if (messageData.cache_invalidations) {
      messageData.cache_invalidations.forEach((key: string[]) => {
        queryClient.invalidateQueries({ queryKey: key });
      });
    }
    if (messageData.force_navigation && messageData.id) {
      // Only navigate if we haven't already processed this message's navigation
      if (!processedNavigations.current.has(messageData.id)) {
        processedNavigations.current.add(messageData.id);
        console.log("Forcing navigation to ", messageData.force_navigation);
        let path = messageData.force_navigation;
        if (!path.startsWith("/")) {
          path = "/" + path;
        }
        path = path.replace(/^\/+/, "/");
        navigate(path);
      }
    }
  }, [
    lastMessage,
    queryClient,
    user.data?.id,
    removeUserFromTyping,
    TYPING_TIMEOUT_DURATION,
    navigate,
  ]);

  // Extract suggested replies from the latest AI message, but only if
  // no user message has been sent after it (i.e. the user hasn't answered yet)
  const suggestedReplies = React.useMemo(() => {
    if (messages.length === 0) return [];
    const lastMessage = messages[messages.length - 1];
    if (lastMessage.sender !== "ai") return [];
    return lastMessage.suggested_replies || [];
  }, [messages]);

  // Start a fresh conversation. The one escape from a conversation that has
  // grown too long (or gone wrong) without navigating away.
  const clearConversation = useCallback(() => {
    // Cancel any message still waiting for the socket to open — otherwise it
    // transmits (and gets echoed + answered) into the fresh conversation, and
    // burns a backend AI run nobody asked for anymore.
    pendingSendIntervalsRef.current.forEach((i) => clearInterval(i));
    pendingSendIntervalsRef.current.clear();
    // If a reply is genuinely in flight (a message already transmitted) when the
    // user starts a fresh chat, arm suppression so that stale reply is dropped
    // when it arrives rather than repopulating the now-empty conversation.
    if (awaitingResponseRef.current) {
      suppressNextAiResponseRef.current = true;
    }
    awaitingResponseRef.current = false;
    processedIncomingIdsRef.current.clear();
    setMessages([]);
    processedNavigations.current.clear();
    typingTimeouts.current.forEach((t) => clearTimeout(t));
    typingTimeouts.current.clear();
    setUsersTyping([]);
  }, [setUsersTyping]);

  const sendMessage = useCallback(
    ({ message, attachments = [], context, silent, handoffMessage }: OutgoingAgentMessage) => {
      // Use passed context if provided, otherwise fall back to currentViewDescription
      const effectiveContext = context ?? currentViewDescription;

      if (!shouldConnect) {
        setShouldConnect(true);
      }

      // Inject a visible handoff marker before the silent message
      if (handoffMessage) {
        setMessages((prev) => [...prev, eventBubble(handoffMessage)]);
      }

      // Optimistically show the user's own message right away. Without this it
      // only appears when the server echoes it back — a visible lag on a slow
      // connection, and nothing at all if the send never lands (looks like the
      // assistant ate the message). The server echo replaces this placeholder
      // (deduped by the _optimistic flag in the lastMessage effect).
      if (!silent && message) {
        setMessages((prev) => [
          ...prev,
          {
            id: `optimistic-${
              typeof crypto !== "undefined" && crypto.randomUUID
                ? crypto.randomUUID()
                : Date.now()
            }`,
            sender: "human",
            content: message,
            user: {
              id: user.data?.id ?? "",
              first_name: user.data?.first_name ?? "",
              last_name: user.data?.last_name ?? "",
              email: user.data?.email ?? "",
            },
            attachments,
            sources: [],
            _optimistic: true,
          } as ChatMessageType,
        ]);
      }

      // Only replay real human/ai turns as text. Historical attachments are
      // NOT re-sent: re-uploading every past image's base64 on each message
      // made long conversations progressively slower and pricier (and could
      // eventually exceed limits). Event bubbles (errors, handoff markers) are
      // UI-only and dropped too.
      const history = messages
        .filter((msg) => msg.sender === "human" || msg.sender === "ai")
        .map((msg) => ({
          sender: msg.sender,
          content: msg.content,
        }));
      const payload: Record<string, unknown> = {
        message,
        history,
        context: effectiveContext,
      };
      if (attachments.length) {
        payload.attachments = attachments.map((a) => ({
          filename: a.filename,
          mime_type: a.mime_type,
          data: a.data,
          size: a.size,
        }));
      }
      if (silent) payload.silent = true;

      // A message counts as pending-reply only once it actually transmits —
      // arming here (not at the optimistic echo) means a queued send cancelled
      // by New chat won't leave suppression armed against a future real reply.
      if (canSendMessagesRef.current) {
        sendJsonMessage(payload);
        awaitingResponseRef.current = true;
        return;
      }

      let attempts = 0;
      const maxAttempts = 20;

      const interval = setInterval(() => {
        attempts++;
        if (canSendMessagesRef.current) {
          sendJsonMessage(payload);
          awaitingResponseRef.current = true;
          pendingSendIntervalsRef.current.delete(interval);
          clearInterval(interval);
        } else if (attempts >= maxAttempts) {
          pendingSendIntervalsRef.current.delete(interval);
          clearInterval(interval);
          // Don't drop the message silently — tell the user it didn't send so
          // they can retry, instead of the input just clearing and nothing
          // ever appearing.
          setMessages((prev) => [
            ...prev,
            eventBubble(
              "Couldn't reach the assistant — please check your connection and try again.",
            ),
          ]);
        }
      }, 500);
      pendingSendIntervalsRef.current.add(interval);

      return () => {
        pendingSendIntervalsRef.current.delete(interval);
        clearInterval(interval);
      };
    },
    [shouldConnect, messages, currentViewDescription, sendJsonMessage, user.data],
  );

  return (
    <AgentContext.Provider
      value={{
        canSendMessages,
        sendMessage,
        messages,
        submitEvent,
        usersTyping,
        setCurrentViewDescription,
        suggestedReplies,
        clearConversation,
      }}
    >
      {children}
    </AgentContext.Provider>
  );
};
